import { useState } from 'react';
import { StatusBadge } from '../../components/StatusBadge';
import {
  createVersion,
  deleteVersion,
  publishVersion,
  unpublishPrompt,
  updateVersion,
  issuesFrom,
  messageFrom,
} from './api';
import { VersionEditor } from './VersionEditor';
import { VersionDiff } from './VersionDiff';
import type { PromptDetail as Detail, PromptValidationIssue, PromptVersion } from './types';

interface Props {
  detail: Detail;
  onChanged: () => void;
}

function VersionStatus({ status }: { status: PromptVersion['status'] }) {
  const published = status === 'published';
  return (
    <span data-testid={`prompt-status-${status}`}>
      <StatusBadge
        label={published ? 'Published · in use' : 'Draft · not in use'}
        tone={published ? 'good' : 'neutral'}
      />
    </span>
  );
}

/**
 * One prompt: what it is running on, what its contract is, and its history.
 *
 * The contract is shown rather than assumed. An operator editing a prompt has
 * no other way to know that `{{historyText}}` is load-bearing or that removing
 * `<service_recommendation>` will quietly stop service cards appearing, and
 * that knowledge lives in the backend registry, not in anyone's head.
 */
export function PromptDetailPanel({ detail, onChanged }: Props) {
  const [editing, setEditing] = useState<PromptVersion | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<PromptValidationIssue[]>([]);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [comparing, setComparing] = useState<PromptVersion | null>(null);

  const { definition, effective, publishedVersionId, versions } = detail;

  /* Compare against what the prompt is *running on*, which is the published
     version when there is one and the text compiled into the release when there
     isn't — the same thing `effective` already resolves to. Comparing against
     an arbitrary other draft would answer a question nobody is asking before
     they publish. */
  const baseline = {
    label: publishedVersionId
      ? (versions.find((v) => v.id === publishedVersionId)?.label ?? 'published version')
      : 'the version shipped in the code',
    en: effective.en,
    es: effective.es,
  };

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    setIssues([]);
    try {
      await action();
      setEditing(null);
      setCreating(false);
      setConfirmingDelete(null);
      onChanged();
    } catch (err) {
      setError(messageFrom(err));
      setIssues(issuesFrom(err));
    } finally {
      setBusy(false);
    }
  };

  const save = (body: { en: string; es: string; label: string; note?: string }) =>
    run(() =>
      editing
        ? updateVersion(definition.key, editing.id, body)
        : createVersion(definition.key, body),
    );

  const requiredTokens = definition.requiredTokens ?? [];

  return (
    <div data-testid="prompt-detail">
      <header className="ps-detail-head">
        <div>
          <h2 className="ps-detail-key">
            {definition.key}
            {publishedVersionId ? (
              <StatusBadge label="Live" tone="good" />
            ) : (
              <span data-testid="prompt-using-seed">
                <StatusBadge label="Running on shipped text" tone="warn" />
              </span>
            )}
          </h2>
          <p className="ps-detail-desc">{definition.description}</p>
        </div>
      </header>

      {error && (
        <div className="error-banner" data-testid="prompt-detail-error">
          {error}
          {issues.length > 0 && (
            <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem' }}>
              {issues.map((issue, i) => (
                <li key={i}>
                  [{issue.language.toUpperCase()}] {issue.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="form-card full-width">
        <h2>Contract</h2>
        <p className="ps-card-note">
          Enforced by the backend on every save. A version that breaks it is refused rather than
          stored.
        </p>

        <div className="subcard-grid ps-contract-grid" style={{ marginTop: '0.4rem' }}>
          <div className="subcard">
            <span className="dashboard-card-label">Placeholders</span>
            <div>
              {definition.variables.length === 0 ? (
                <span className="ps-chip none">none — takes no runtime context</span>
              ) : (
                definition.variables.map((name) => (
                  <code key={name} className="ps-chip">{`{{${name}}}`}</code>
                ))
              )}
            </div>
          </div>

          {requiredTokens.length > 0 && (
            <div className="subcard">
              <span className="dashboard-card-label">Required tokens</span>
              <div>
                {requiredTokens.map((token) => (
                  <code key={token} className="ps-chip token">
                    {token}
                  </code>
                ))}
              </div>
              <p className="ps-card-note">
                Backend code parses these out of the model&rsquo;s reply. Removing one breaks the
                feature with no error anywhere.
              </p>
            </div>
          )}
        </div>
      </div>

      {(creating || editing) && (
        <VersionEditor
          definition={definition}
          version={editing}
          seed={effective}
          saving={busy}
          onCancel={() => {
            setEditing(null);
            setCreating(false);
            setError(null);
            setIssues([]);
          }}
          onSave={save}
          externalIssues={issues}
          externalError={error}
        />
      )}

      {comparing && (
        <VersionDiff baseline={baseline} version={comparing} onClose={() => setComparing(null)} />
      )}

      <div className="form-card full-width">
        <div className="ps-card-head">
          <h2>Versions</h2>
          <div className="ps-actions">
            {publishedVersionId && (
              <button
                type="button"
                onClick={() => run(() => unpublishPrompt(definition.key))}
                disabled={busy}
                data-testid="prompt-unpublish"
                className="btn btn-secondary"
              >
                Unpublish
              </button>
            )}
            {!creating && !editing && (
              <button
                type="button"
                onClick={() => setCreating(true)}
                data-testid="prompt-new-draft"
                className="btn btn-primary"
              >
                New draft
              </button>
            )}
          </div>
        </div>

        {versions.length === 0 ? (
          <div className="empty-state">
            No saved versions. This prompt is running on the text shipped in the code.
          </div>
        ) : (
          <div className="ps-versions">
            {versions.map((version) => {
              const isPublished = version.id === publishedVersionId;
              return (
                <div
                  key={version.id}
                  data-testid={`prompt-version-${version.id}`}
                  className={`ps-version${isPublished ? ' published' : ''}`}
                >
                  <div className="ps-version-main">
                    <div className="ps-version-title">
                      {version.label}
                      <VersionStatus status={version.status} />
                    </div>
                    {version.note && <p className="ps-version-note">{version.note}</p>}
                    <p className="ps-version-meta">
                      {new Date(version.createdAt).toLocaleString()} · {version.createdBy}
                      {version.publishedAt &&
                        ` · last published ${new Date(version.publishedAt).toLocaleString()}`}
                    </p>
                  </div>

                  <div className="ps-version-actions">
                    {version.status === 'draft' && (
                      <button
                        type="button"
                        onClick={() => run(() => publishVersion(definition.key, version.id))}
                        disabled={busy}
                        data-testid={`prompt-publish-${version.id}`}
                        className="btn btn-primary"
                      >
                        Publish
                      </button>
                    )}

                    {!isPublished && (
                      <button
                        type="button"
                        onClick={() => setComparing(comparing?.id === version.id ? null : version)}
                        data-testid={`prompt-compare-${version.id}`}
                        className="btn btn-secondary"
                      >
                        {comparing?.id === version.id ? 'Hide diff' : 'Compare'}
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() =>
                        version.status === 'published'
                          ? run(() =>
                              createVersion(definition.key, {
                                fromVersionId: version.id,
                                label: `Copy of ${version.label}`,
                              }),
                            )
                          : (setEditing(version), setCreating(false))
                      }
                      disabled={busy}
                      data-testid={`prompt-edit-${version.id}`}
                      className="btn btn-secondary"
                    >
                      {/* Published versions are immutable, so "edit" forks a draft rather
                          than pretending the live text can be changed in place. */}
                      {version.status === 'published' ? 'Edit as new draft' : 'Edit'}
                    </button>

                    {version.status === 'draft' &&
                      (confirmingDelete === version.id ? (
                        <>
                          <button
                            type="button"
                            onClick={() => run(() => deleteVersion(definition.key, version.id))}
                            disabled={busy}
                            data-testid={`prompt-delete-confirm-${version.id}`}
                            className="btn btn-danger"
                          >
                            Confirm delete
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmingDelete(null)}
                            className="btn btn-secondary"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmingDelete(version.id)}
                          data-testid={`prompt-delete-${version.id}`}
                          className="btn btn-secondary"
                        >
                          Delete
                        </button>
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
